/* eslint-disable import/no-webpack-loader-syntax */
/* eslint-disable import/no-unresolved */
import {
    FC, useRef, useCallback, useEffect,
} from 'react';
import Map, {
    Source, Layer, NavigationControl, Marker,
} from 'react-map-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';

import { MAPBOX_ACCESS_TOKEN } from '../../../const/environments.constants';
import { useCoverageContext } from './context/coverage.ctx';
import { getPolygonCenter, getAllCoveragesBounds, normalizePolygon } from './utils/polygon';

import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

const DEFAULT_CENTER = { lng: -78.4678, lat: -0.1807 };
const DEFAULT_ZOOM = 11;

interface CoverageMapMapboxProps {
    pin?: { lng: number; lat: number } | null;
    flyTo?: { lng: number; lat: number } | null;
    onFlyToComplete?: () => void;
    onLoaded: () => void;
    mapboxErrorHandler: (event: { error?: { status?: number; message?: string }; sourceId?: string }) => void;
}

const CoverageMapMapbox: FC<CoverageMapMapboxProps> = ({
    pin, flyTo, onFlyToComplete, onLoaded, mapboxErrorHandler,
}) => {
    const {
        coverages,
        selected,
        isDrawing,
        setSelected,
        handleDrawComplete,
    } = useCoverageContext();

    const mapRef = useRef<any>(null);
    const drawRef = useRef<any>(null);

    const getCenter = useCallback(() => {
        if (coverages.length > 0) {
            return getPolygonCenter(coverages[0].polygon);
        }
        return DEFAULT_CENTER;
    }, [coverages]);

    const handleMapLoad = useCallback((e: any) => {
        const map = e.target;
        mapRef.current = map;

        map.on('error', mapboxErrorHandler);

        const draw = new MapboxDraw({
            displayControlsDefault: false,
            controls: { polygon: false, trash: false },
            defaultMode: 'simple_select',
        });

        map.addControl(draw);
        drawRef.current = draw;

        map.on('draw.create', (event: any) => {
            const feature = event.features[0];
            if (feature?.geometry?.type === 'Polygon') {
                const coords = feature.geometry.coordinates[0].map(
                    (c: number[]) => [c[0], c[1]],
                );
                handleDrawComplete(coords);
                draw.deleteAll();
            }
        });

        const bounds = getAllCoveragesBounds(coverages);
        if (bounds) {
            map.fitBounds(bounds, { padding: 60, duration: 0 });
        }

        onLoaded();
    }, [handleDrawComplete, coverages, mapboxErrorHandler, onLoaded]);

    useEffect(() => {
        if (!drawRef.current) return;

        if (isDrawing) {
            drawRef.current.changeMode('draw_polygon');
        } else {
            drawRef.current.changeMode('simple_select');
            drawRef.current.deleteAll();
        }
    }, [isDrawing]);

    useEffect(() => {
        if (!mapRef.current || !selected) return;

        const center = getPolygonCenter(selected.polygon);
        mapRef.current.flyTo({ center: [center.lng, center.lat], zoom: 13, duration: 800 });
    }, [selected]);

    useEffect(() => {
        if (!mapRef.current || !flyTo) return;

        mapRef.current.flyTo({ center: [flyTo.lng, flyTo.lat], zoom: 14, duration: 800 });
        onFlyToComplete?.();
    }, [flyTo, onFlyToComplete]);

    const handleMapClick = useCallback((e: any) => {
        if (isDrawing) return;

        const features = mapRef.current?.queryRenderedFeatures(e.point, {
            layers: ['coverage-fill'],
        });

        if (features?.length) {
            const clickedId = features[0].properties?.id;
            const found = coverages.find((c) => c._id === clickedId);
            if (found) setSelected(found);
        } else {
            setSelected(null);
        }
    }, [isDrawing, coverages, setSelected]);

    // eslint-disable-next-line no-undef
    const geojson: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: coverages.map((c) => {
            const normalized = normalizePolygon(c.polygon);
            return {
                type: 'Feature' as const,
                properties: {
                    id: c._id,
                    name: c.name,
                    isVisible: c.isVisible,
                    isSelected: selected?._id === c._id,
                },
                geometry: {
                    type: 'Polygon' as const,
                    coordinates: normalized.length >= 3 ? [normalized] : [],
                },
            };
        }),
    };

    const center = getCenter();

    return (
        <Map
            // @ts-ignore
            mapLib={import('!mapbox-gl')}
            mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
            initialViewState={{
                longitude: center.lng,
                latitude: center.lat,
                zoom: DEFAULT_ZOOM,
            }}
            style={{ width: '100%', height: '100%' }}
            mapStyle="mapbox://styles/mapbox/light-v11"
            onLoad={handleMapLoad}
            onClick={handleMapClick}
            interactiveLayerIds={['coverage-fill']}
        >
            <NavigationControl position="top-right" />

            {coverages.map((c) => {
                const labelCenter = getPolygonCenter(c.polygon);
                return (
                    <Marker
                        key={`label-${c._id}`}
                        longitude={labelCenter.lng}
                        latitude={labelCenter.lat}
                        anchor="center"
                    >
                        <div style={{
                            background: c.isVisible ? 'var(--color-purple)' : 'rgba(255,255,255,0.9)',
                            padding: '2px 8px',
                            borderRadius: 4,
                            fontSize: 12,
                            fontWeight: 600,
                            color: c.isVisible ? '#fff' : 'var(--color-dark)',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                        }}
                        >
                            {c.name}
                        </div>
                    </Marker>
                );
            })}

            {pin && (
                <Marker
                    longitude={pin.lng}
                    latitude={pin.lat}
                    anchor="bottom"
                >
                    <div style={{
                        width: 32,
                        height: 32,
                        background: 'var(--color-purple)',
                        borderRadius: '50% 50% 50% 0',
                        transform: 'rotate(-45deg)',
                        border: '3px solid white',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                    }}
                    />
                </Marker>
            )}

            <Source id="coverages" type="geojson" data={geojson}>
                <Layer
                    id="coverage-fill"
                    type="fill"
                    paint={{
                        'fill-color': [
                            'case',
                            ['get', 'isSelected'], '#6C8EEF',
                            ['get', 'isVisible'], '#52c41a',
                            '#bfbfbf',
                        ],
                        'fill-opacity': [
                            'case',
                            ['get', 'isSelected'], 0.4,
                            0.25,
                        ],
                    }}
                />
                <Layer
                    id="coverage-outline"
                    type="line"
                    paint={{
                        'line-color': [
                            'case',
                            ['get', 'isSelected'], '#6C8EEF',
                            ['get', 'isVisible'], '#52c41a',
                            '#bfbfbf',
                        ],
                        'line-width': [
                            'case',
                            ['get', 'isSelected'], 3,
                            2,
                        ],
                    }}
                />
            </Source>
        </Map>
    );
};

export default CoverageMapMapbox;
